import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { completeOAuthRedirect } from "@/lib/auth-callback";
import { takeIntendedPath } from "@/lib/auth-redirect";

export const Route = createFileRoute("/auth/")({
  staticData: { sitemap: false },
  validateSearch: (search: Record<string, unknown>): { reden?: "ingetrokken" | "geen-toegang" } =>
    search["reden"] === "ingetrokken" || search["reden"] === "geen-toegang" ? { reden: search["reden"] } : {},
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const { reden } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Vangnet: als het aanmeldbewijs toch op deze pagina belandt, ronden we het hier af.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await completeOAuthRedirect();
      if (cancelled) return;
      if (result.status === "ok") navigate({ to: takeIntendedPath(), replace: true });
      if (result.status === "error") setMessage(result.message);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    navigate({ to: takeIntendedPath() });
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage(null);

    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/auth/callback`,
    });

    setLoading(false);

    if (result.error) {
      setMessage(result.error instanceof Error ? result.error.message : String(result.error));
      return;
    }

    if (result.redirected) {
      // Browser redirects to Google; stop here.
      return;
    }

    // Tokens received and session set in the preview iframe flow.
    navigate({ to: takeIntendedPath() });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Beheerconsole</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Meld u aan om de prestatiedashboards te bekijken.
          </p>
        </div>

        {reden === "ingetrokken" && (
          <p role="status" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            Uw toegang is ingetrokken. Neem contact op met de beheerder.
          </p>
        )}
        {reden === "geen-toegang" && (
          <p role="status" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            U heeft nog geen toegang. Vraag de beheerder van Gopro Projects om u uit te nodigen.
          </p>
        )}

        <form onSubmit={handleSignIn} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Bezig…" : "Aanmelden"}
          </Button>
        </form>

        <div className="space-y-4">
          <Separator />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {loading ? "Bezig…" : "Aanmelden met Google"}
          </Button>
        </div>

        {message ? (
          <p className="text-sm text-destructive" role="alert">{message}</p>
        ) : null}
      </div>
    </div>
  );
}
